<template>

  <!-- Use Carbon Charts to display stacked activity chart. -->
   <button class="btn btn-sm btn-outline-primary" @click="loadActivity">Refresh Activity</button>
  <div ref="chartRef" class="carbon-chart-container">
    <div class="loading">Loading...</div>
  </div>

</template>

<script setup>
import { ref, onMounted } from "vue";
import { StackedBarChart } from "@carbon/charts";
import ApiService from "@/services/ApiService";
import Helper from "@/services/Helper"
import "@carbon/charts/styles.css";
import {min} from "d3";

const chartRef = ref(null);
let chartData = null;
let timeout = null;

const hhmmss = (ms) => {
  let seconds = ms / 1000;
  const hours = parseInt( seconds / 3600 );
  seconds = seconds % 3600;
  const minutes = parseInt( seconds / 60 );
  seconds = seconds % 60;
  
  return ("0" + hours).slice(-2) + ":" +
    ("0" + minutes).slice(-2) + ":" +
    ("0" + seconds).slice(-2);
}

const loadActivity = async () =>
{
  // Clear any existing timeout to prevent overlapping calls
  if (timeout) {
    clearTimeout(timeout);
    timeout = null;
  }
  
  let now = new Date();
  
  // Calculate date from a week ago
  let weekAgo = new Date(now);
  weekAgo.setDate(weekAgo.getDate() - 7);
  
  let res = await ApiService.loadNotes({
    Page: 0,
    Size: 1000000000,
    StartDate: Helper.dateToInput(weekAgo, Helper.DateFormat_Date) + " 00:00:00",
    // EndDate: Helper.dateToInput(now, Helper.DateFormat_Date) + " 23:59:59",
    IncludeRequest: true
  }, true);

  let notes = res.Data;
  
  // Filter to only include notes from the last 24 hours
  const twentyFourHoursAgo = now.getTime() - (24 * 60 * 60 * 1000);
  notes = notes.filter(note => note.DateTakenAsDate && note.DateTakenAsDate.getTime() >= twentyFourHoursAgo);
  
  // order notes by DateTaken
  notes.sort((a, b) => {
    return (b.DateTakenAsDate - a.DateTakenAsDate);
  });
  
  console.debug("sorted", notes);
  console.debug("filtered notes count:", notes.length);
  
  // filter the last note of the user
  let data = {};
  for (let i = 0; i < notes.length; i++) {
    if (!data["_" + notes[i].UserId]) {
      data["_" + notes[i].UserId] = notes[i];
    }
  }
  
  // prep the data for the graph
  let graph = [];
  for (let key in data) {
    let row = data[key];
    
    // diff in seconds ...
    let seconds = (now.getTime() - row.DateTakenAsDate.getTime()) / 1000;
    
    graph.push({
      group: "User Activity",
      key: row.Technician?.TechnicianName || "Name Not Set",
      value: Number((seconds / 3600).toFixed(2))
    });
    
  }
  
  let time = Helper.dateToInput(now, Helper.DateFormat_TimeWithSeconds);
  
  const options = {
    title: "User Activity (Since: " + time + ")",
    axes: {
      bottom: {
        stacked: true,
        // mapsTo: "value",
        // scaleType: "linear",
      },
      left: {
        // mapsTo: "group",
        scaleType: "labels"
      }
    },
    bars: {
      padding: 0.25
    },
    height: "500px",
  };
  
  // Verify chartRef exists first
  if (!chartRef.value) {
    console.error("Chart ref not available");
    return;
  }
  
  if (graph.length === 0) {
    console.warn("No activity data to display");
    // Destroy existing chart if present
    if (chartData) {
      try {
        chartData.destroy();
        chartData = null;
      } catch (e) {
        console.error("Error destroying chart:", e);
      }
    }
    chartRef.value.innerHTML = "<div class='p-3 text-center'>No activity data in the last 24 hours</div>";
    // Still schedule next update
    timeout = setTimeout(loadActivity, (60 * 1000));
    return;
  }
  
  console.debug("Creating chart with data:", graph);
  
  // Destroy existing chart before creating a new one
  if (chartData) {
    try {
      console.debug("Destroying existing chart");
      chartData.destroy();
      chartData = null;
    } catch (e) {
      console.error("Error destroying chart:", e);
    }
  }
  
  // Clear the container - destroy() may have removed child elements
  chartRef.value.innerHTML = "";
  
  // Recreate a container div for the chart since destroy() removes it
  const chartContainer = document.createElement('div');
  chartContainer.className = 'chart-holder';
  chartRef.value.appendChild(chartContainer);
  
  // Always create a fresh chart instance
  try {
    chartData = new StackedBarChart(chartContainer, {
      data: graph, 
      options
    });
    console.debug("Chart created successfully");
  } catch (e) {
    console.error("Error creating chart:", e);
  }
  
  // run every minute
  timeout = setTimeout(loadActivity, (60 * 1000));
};

onMounted(() => {
  loadActivity();
});

</script>